import styled, { css, keyframes } from 'styled-components';
import { theme } from '../../themes';

const titleAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
`;

export const Content = styled.div<{oneColumn?: boolean}>`
  display: flex;
  font-family: ${theme.fonts.lato};

  > div {
    position: relative;

    display: flex;
    flex-direction: column;

    :first-of-type {
      margin-right: auto;
    }

    ${(p) => !p.oneColumn ? `
      :last-of-type {
        margin-left: auto;
      }
    ` : ''}
  }
`;

export const Divider = styled.hr<{marginHorizontal?: number; marginVertical?: number; light?: boolean}>`
  color: ${(p) => p.light ? theme.colors.veryLightGray : theme.colors.grayishBlue};
  border: 1px solid ${(p) => p.light ? theme.colors.veryLightGray : theme.colors.grayishBlue};
  margin: ${(p) => p.marginVertical ? `${p.marginVertical}px` : 0} ${(p) => p.marginHorizontal ? `${p.marginHorizontal}px` : 0};
`;

export const Step = styled.div<{marginBottom?: number; smallTitle?: boolean}>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  > div:nth-of-type(1) {
    > img {
      position: relative;
      top: 8px;
      width: 121px;
      height: 38px;
      margin-right: 32px;
    }
  
    > span {
      color: ${theme.colors.vividOrange};
      font-family: ${theme.fonts.lato};
      font-size: ${(p) => p.smallTitle ? 29 : 36}px;
      font-weight: 700;
      line-height: 43px;
      letter-spacing: 0em;
      text-align: left;
      text-transform: uppercase;
    }
  }

  > div:nth-of-type(2) {
    display: flex;
    gap: 16px;
  }

  ${(p) => p.smallTitle ? 'margin-bottom: 40px;' : ''}
  ${(p) => p.marginBottom || p.smallTitle ? `margin-bottom: ${(p.marginBottom || 0) + (p.smallTitle ? -3 : 0)}px;` : ''}
`;

export const Text = styled.div<{marginTop?: number; marginBottom?: number}>`
  color: ${theme.colors.black};

  font-size: 22px;
  font-weight: 500;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: left;

  user-select: none;

  ${(p) => p.marginTop ? `
    margin-top: ${p.marginTop}px;
  ` : ''}

  ${(p) => p.marginBottom ? `
    margin-bottom: ${p.marginBottom}px;
  ` : ''}
`;

export const HintText = styled.div<{marginTop?: number; marginBottom?: number; width?: number; height?: number; error?: boolean}>`
  color: ${(p) => p.error ? theme.colors.vividRed : theme.colors.grayishBlue};

  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: left;

  ${(p) => p.marginTop ? `
    margin-top: ${p.marginTop}px;
  ` : ''}

  ${(p) => p.marginBottom ? `
    margin-bottom: ${p.marginBottom}px;
  ` : ''}

  ${(p) => p.width ? `
    width: ${p.width}px;
  ` : ''}

  ${(p) => p.height ? `
    height: ${p.height}px;
  ` : ''}
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  height: 43px;

  > div:last-of-type {
    display: flex;
    gap: 16px;
  }
`;

export const Waiting = styled.img`
  width: 500px;
  height: 333px;
  margin-right: 100px;
`;

export const CarInfoTable = styled.div`
  font-family: ${theme.fonts.lato};
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: left;
  min-width: 500px;

  margin-top: 48px;

  > table {
    width: 600px;
    border-spacing: 0;

    > tbody > tr {
      height: 48px;

      :nth-of-type(2n) {
        background: ${theme.colors.whiteSmoke};
      }

      > td {
        width: 50%;
        padding: 12px;

        :first-of-type {
          border-right: 1px solid ${theme.colors.veryLightGray};
          vertical-align: top;
        }

        > div {
          max-height: 192px;
          overflow: auto;
        }
      }
    }
  }
`;

export const Pair = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

export const PairWithLabels = styled.div`
  display: flex;
  justify-content: space-between;

  > div {
    display: flex;
    flex-direction: column;
  }
`;

export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const sizes = {
  large: `
    font-size: 36px;
    font-weight: 700;
    line-height: 43px;
  `,
  medium: `
    font-size: 22px;
    font-weight: 500;
    line-height: 26px;
  `,
  small: `
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
  `,
};

export const Title = styled.div<{size: 'large' | 'medium' | 'small'; marginBottom?: number; marginTop?: number; height?: number; animated?: boolean; underline?: boolean; width?: string}>`
  color: ${theme.colors.vividOrange};

  font-family: ${theme.fonts.lato};

  ${() => (p) => sizes[p.size]}

  letter-spacing: 0em;
  text-align: left;

  ${(p) => p.marginBottom ? `margin-bottom: ${p.marginBottom}px;` : ''}
  ${(p) => p.marginTop ? `margin-top: ${p.marginTop}px;` : ''}
  ${(p) => p.height ? `height: ${p.height}px;` : ''}

  animation: ${(p) => p.animated ? css`${titleAnimation} 1s ease-in-out` : 'none'};

  ${(p) => p.underline ? `
    ::after {
      content: "";
      display: block;
      width: 100%;
      margin-top: 4px;
      border-top: 1px ${theme.colors.veryLightGray} solid;
    }
  ` : ''}

  ${(p) => p.width ? `
    width: ${p.width};
    text-align: center;
  ` : ''}
`;

export const ButtonsStyle = styled.div.withConfig({
  shouldForwardProp: (prop) => !['bottom'].includes(prop as string),
})<{bottom?: boolean}>`
  display: flex;
  align-content: center;
  gap: 16px;

  ${(p) => p.bottom ? `
    position: absolute;
    bottom: 0;
    right: 0;
  ` : ''}
`;
